import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function Login() {
  const { login, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || null;
  const reason = params.get("reason");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [needsPin, setNeedsPin] = useState(false);
  const [needsPinSetup, setNeedsPinSetup] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Surface session-expired bounces from the 401 interceptor.
  useEffect(() => {
    if (reason === "session_expired") {
      toast.info("Your session expired. Please sign in again.");
    }
  }, [reason]);

  // The backend wraps shared-email PIN signals in HTTPException details as
  // structured objects. They come back as either {pin_required: true} or
  // {setup_pin_required: true}.
  const extractFlag = (detail, flag) =>
    !!(detail && typeof detail === "object" && detail[flag]);

  const finishSignIn = (user) => {
    toast.success(`Welcome${user?.name ? ", " + user.name.split(" ")[0] : ""}.`);
    // Honor `?next=` from session-expired bounces; otherwise default by role.
    if (nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//")) {
      nav(nextPath);
      return;
    }
    nav(user?.role === "facilitator" || user?.role === "admin" ? "/facilitator" : "/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const user = await login(email, password, needsPin ? pin : undefined);
      finishSignIn(user);
    } catch (e2) {
      if (extractFlag(e2.detail, "pin_required")) {
        setNeedsPin(true);
        setErr("This email is shared. Enter your 4-digit PIN.");
      } else if (extractFlag(e2.detail, "setup_pin_required")) {
        setNeedsPinSetup(true);
        setErr("Your email is now shared with other accounts. Set a 4-digit PIN to continue.");
      } else {
        setErr(e2.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitPinSetup = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(newPin)) {
      setErr("PIN must be exactly 4 digits.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/setup-pin", { email, password, pin: newPin });
      await refresh();
      toast.success("PIN saved. You're signed in.");
      finishSignIn(data.user);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Could not save PIN.");
    } finally {
      setLoading(false);
    }
  };

  const showEmailPasswordForm = !needsPinSetup;

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 grain relative" data-testid="login-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(201,169,97,0.08),_transparent_60%)]" />
      <div className="relative card-bento w-full max-w-md">
        <div className="overline mb-3">{needsPinSetup ? "Set Your PIN" : "Sign In"}</div>
        <h1
          className="text-3xl md:text-4xl text-[#F7F5F0] mb-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {needsPinSetup ? "One quick step." : "Continue your path."}
        </h1>
        <p className="text-sm text-[#8A8782] mb-8">
          {needsPinSetup
            ? "Your email is now shared with another account. Choose a 4-digit PIN that only you know."
            : "The crown is not given. It is earned, day by day."}
        </p>

        {showEmailPasswordForm && (
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-tcpi"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-tcpi"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            {needsPin && (
              <div data-testid="pin-field-wrap">
                <label className="block text-sm text-[#D1CFC9] mb-2 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#C9A961]" />
                  4-digit PIN
                </label>
                <input
                  type="password"
                  required
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  className="input-tcpi tracking-[0.4em] text-center"
                  placeholder="• • • •"
                  data-testid="login-pin-input"
                  autoFocus
                />
                <p className="text-[11px] text-[#8A8782] mt-1.5 italic">
                  This email is shared by other accounts. Your PIN is what proves it's you.
                </p>
              </div>
            )}

            {err && (
              <div className="text-sm text-red-400" data-testid="login-error">
                {err}
                {/Network error|Server is unavailable|Server error/i.test(err) && (
                  <div className="mt-1 text-xs text-[#8A8782]">
                    If this persists right after a redeploy, wait ~30 seconds for
                    the backend to finish booting and try again.
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {(() => {
                if (loading) return "Signing in…";
                return needsPin ? "Verify & Sign In" : "Sign In";
              })()}
            </button>
          </form>
        )}

        {needsPinSetup && (
          <form onSubmit={submitPinSetup} className="space-y-5" data-testid="pin-setup-form">
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5 text-[#C9A961]" />
                Choose a 4-digit PIN
              </label>
              <input
                type="password"
                required
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="input-tcpi tracking-[0.4em] text-center"
                placeholder="• • • •"
                data-testid="pin-setup-input"
                autoFocus
              />
              <p className="text-[11px] text-[#8A8782] mt-1.5 italic">
                Pick something memorable but not obvious. You'll use this every time you sign in.
              </p>
            </div>
            {err && (
              <div className="text-sm text-red-400" data-testid="pin-setup-error">{err}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
              data-testid="pin-setup-submit"
            >
              {loading ? "Saving…" : "Save PIN & Sign In"}
            </button>
            <button
              type="button"
              onClick={() => { setNeedsPinSetup(false); setNewPin(""); setErr(""); }}
              className="block w-full text-xs text-[#8A8782] hover:text-[#C9A961]"
            >
              Cancel
            </button>
          </form>
        )}

        <div className="mt-6 text-sm text-center text-[#8A8782] space-y-2">
          <div>
            <Link to="/forgot-password" className="text-[#C9A961] hover:underline" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>
          <div>
            New here?{" "}
            <Link to="/register" className="text-[#C9A961] hover:underline" data-testid="login-go-register">
              Enroll now
            </Link>
          </div>
          <p className="text-[11px] text-[#8A8782] italic pt-2 leading-relaxed" data-testid="login-facilitator-hint">
            Still stuck? If you're enrolled through a cohort or partner program, your
            facilitator can reset your password for you.
          </p>
        </div>
      </div>
    </div>
  );
}
